import type { SsoProviderKind } from '@zeus/index'
import { useMutation, useQuery } from '@apollo/client/react'
import { Check, Loader, Trash2, TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import {
  GQL_SET_SSO_ENABLED,
  GQL_SSO_PRESETS,
  GQL_SSO_PROVIDER_CONFIGS,
  GQL_SSO_SETTINGS,
  GQL_UPSERT_SSO_PROVIDER,
} from '~/gql/sso/sso'
import { tourAnchor } from '~/helpers/tour/anchors'
import SsoProviderDeleteModal from './components/SsoProviderDeleteModal'

/** Saisie d'une carte du catalogue, avant envoi à l'API. */
type ProviderForm = {
  label: string
  enabled: boolean
  issuer: string
  clientId: string
  /** Toujours vide au chargement : l'API ne renvoie jamais le secret. */
  clientSecret: string
  entryPoint: string
  idpIssuer: string
  idpCert: string
  /** Saisie libre, séparée par virgules ou espaces. */
  allowedDomains: string
}

const EMPTY_FORM: ProviderForm = {
  label: '',
  enabled: false,
  issuer: '',
  clientId: '',
  clientSecret: '',
  entryPoint: '',
  idpIssuer: '',
  idpCert: '',
  allowedDomains: '',
}

/**
 * Menu d'administration du SSO (#40).
 *
 * Le catalogue de fournisseurs vient de l'API : l'écran ne code en dur ni la
 * liste, ni les issuers par défaut. Chaque carte est indépendante — on
 * enregistre un fournisseur à la fois, pour ne pas invalider les autres avec
 * une saisie incomplète.
 */
function SsoSettings() {
  const { t } = useTranslation()
  const { data: presetsData, loading: presetsLoading } = useQuery(GQL_SSO_PRESETS)
  const { data: configsData, loading: configsLoading, refetch } = useQuery(GQL_SSO_PROVIDER_CONFIGS)
  const {
    data: settingsData,
    loading: settingsLoading,
    refetch: refetchSettings,
  } = useQuery(GQL_SSO_SETTINGS)
  const [upsertProvider] = useMutation(GQL_UPSERT_SSO_PROVIDER)
  const [setSsoEnabled] = useMutation(GQL_SET_SSO_ENABLED)

  // Saisies en cours, par fournisseur. Ne contient QUE ce que l'admin a touché :
  // le reste est dérivé des queries ci-dessous, sans copie en état.
  const [edits, setEdits] = useState<Record<string, Partial<ProviderForm>>>({})
  const [busyKind, setBusyKind] = useState<SsoProviderKind | null>(null)
  const [togglingSso, setTogglingSso] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<SsoProviderKind | null>(null)
  // Fournisseur en cours d'édition. `null` = aucun choix explicite, on retombe
  // sur le défaut calculé plus bas.
  const [picked, setPicked] = useState<SsoProviderKind | null>(null)

  const ssoEnabled = settingsData?.ssoSettings.enabled ?? false

  const presets = presetsData?.ssoPresets ?? []
  const configs = configsData?.ssoProviderConfigs ?? []
  const configByKind = new Map(configs.map(config => [config.kind as string, config]))

  /**
   * État serveur remis en forme de formulaire.
   *
   * Dérivé, plutôt que recopié dans un `useState` par un `useEffect` : la copie
   * marchait, mais elle dédoublait l'état des queries et coûtait un rendu de
   * plus à chaque `refetch`. Les saisies de l'admin vivent à côté, dans `edits`,
   * ce qui laisse une seule source de vérité pour l'enregistré.
   */
  const saved = useMemo(() => {
    const next: Record<string, ProviderForm> = {}
    for (const preset of presetsData?.ssoPresets ?? []) {
      const config = configsData?.ssoProviderConfigs.find(item => item.kind === preset.kind)
      next[preset.kind as string] = {
        ...EMPTY_FORM,
        label: config?.label ?? preset.defaultLabel ?? '',
        enabled: config?.enabled ?? false,
        issuer: config?.issuer ?? preset.defaultIssuer ?? '',
        clientId: config?.clientId ?? '',
        entryPoint: config?.entryPoint ?? '',
        idpIssuer: config?.idpIssuer ?? '',
        idpCert: config?.idpCert ?? '',
        allowedDomains: (config?.allowedDomains ?? []).join(', '),
      }
    }
    return next
  }, [presetsData, configsData])

  /** Valeur affichée : la saisie en cours si elle existe, sinon l'enregistré. */
  const formFor = (kind: string): ProviderForm => ({
    ...EMPTY_FORM,
    ...saved[kind],
    ...edits[kind],
  })

  const set = (kind: SsoProviderKind) => <K extends keyof ProviderForm>(key: K, value: ProviderForm[K]) =>
    setEdits(prev => ({ ...prev, [kind]: { ...prev[kind], [key]: value } }))

  /** Après un aller-retour serveur, les saisies locales n'ont plus lieu d'être. */
  const clearEdits = (kind: SsoProviderKind) =>
    setEdits(prev => ({ ...prev, [kind]: {} }))

  /**
   * Fournisseur affiché : celui que l'admin a choisi, sinon le premier qu'il a
   * déjà configuré, sinon la tête du catalogue.
   *
   * L'organisation peut en activer plusieurs — ce sélecteur ne fait que désigner
   * lequel on édite, il ne remplace rien. Ceux qui ne sont pas à l'écran restent
   * configurés et actifs ; le récapitulatif sous le sélecteur les rappelle, pour
   * qu'ils ne disparaissent pas de la vue de l'admin en disparaissant de l'écran.
   */
  const defaultKind = configs[0]?.kind ?? presets[0]?.kind ?? null
  const activeKind = picked ?? defaultKind
  const activePreset = presets.find(preset => preset.kind === activeKind)

  /** Fournisseurs déjà en base, pour le récapitulatif et les repères du menu. */
  const configuredSummary = configs.map(config => ({
    kind: config.kind,
    label: config.label,
    enabled: config.enabled,
  }))

  const handleSave = async (kind: SsoProviderKind) => {
    const form = formFor(kind)

    setBusyKind(kind)
    try {
      await upsertProvider({
        variables: {
          input: {
            kind,
            label: form.label || undefined,
            enabled: form.enabled,
            issuer: form.issuer || undefined,
            clientId: form.clientId || undefined,
            // Omis quand le champ est laissé vide : le secret déjà enregistré
            // est conservé, l'admin n'a pas à le ressaisir à chaque édition.
            clientSecret: form.clientSecret || undefined,
            entryPoint: form.entryPoint || undefined,
            idpIssuer: form.idpIssuer || undefined,
            idpCert: form.idpCert || undefined,
            allowedDomains: form.allowedDomains
              .split(/[\s,;]+/)
              .map(domain => domain.trim())
              .filter(Boolean),
          },
        },
      })
      await refetch()
      clearEdits(kind)
      toast.success(t('sso.saved'))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('sso.saveError'))
    } finally {
      setBusyKind(null)
    }
  }

  const handleToggleSso = async (enabled: boolean) => {
    setTogglingSso(true)
    try {
      await setSsoEnabled({ variables: { enabled } })
      await refetchSettings()
      toast.success(enabled ? t('sso.master.turnedOn') : t('sso.master.turnedOff'))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('sso.saveError'))
    } finally {
      setTogglingSso(false)
    }
  }

  const handleDeleted = async (kind: SsoProviderKind) => {
    await refetch()
    clearEdits(kind)
  }

  if (presetsLoading || configsLoading || settingsLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <p className="text-sm text-muted-foreground" {...tourAnchor('sso.intro')}>{t('sso.intro')}</p>

      <Card {...tourAnchor('sso.master')}>
        <CardHeader className="flex-row items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <CardTitle>{t('sso.master.title')}</CardTitle>
            <p className="text-xs text-muted-foreground">{t('sso.master.help')}</p>
            {!ssoEnabled && (
              <p className="flex items-start gap-1.5 text-xs font-medium text-warning-text">
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                {t('sso.master.off')}
              </p>
            )}
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={ssoEnabled}
              disabled={togglingSso}
              onChange={e => handleToggleSso(e.target.checked)}
            />
            {t('sso.field.enabled')}
          </label>
        </CardHeader>
      </Card>

      <Card {...tourAnchor('sso.picker')}>
        <CardHeader className="gap-1">
          <CardTitle>{t('sso.picker.title')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('sso.picker.help')}</p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Select
            value={activeKind ?? undefined}
            onValueChange={value => setPicked(value as SsoProviderKind)}
          >
            <SelectTrigger aria-label={t('sso.picker.title')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {presets.map((preset) => {
                const config = configByKind.get(preset.kind)
                // Le repère de statut vit dans le libellé de l'option : le
                // composant Select place tous ses enfants dans `ItemText`, donc
                // un badge séparé remonterait aussi dans le bouton fermé.
                const status = config
                  ? config.enabled ? t('sso.picker.status.enabled') : t('sso.picker.status.draft')
                  : null

                return (
                  <SelectItem key={preset.kind} value={preset.kind}>
                    {preset.defaultLabel}
                    {' · '}
                    {preset.protocol}
                    {status ? ` — ${status}` : ''}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>

          {configuredSummary.length > 0
            ? (
                <p className="text-xs text-muted-foreground">
                  {t('sso.picker.configured')}
                  {' '}
                  {configuredSummary
                    .map(item => `${item.label} (${item.enabled
                      ? t('sso.picker.status.enabled')
                      : t('sso.picker.status.draft')})`)
                    .join(', ')}
                </p>
              )
            : (
                <p className="text-xs text-muted-foreground">{t('sso.picker.none')}</p>
              )}
        </CardContent>
      </Card>

      {activePreset && (() => {
        const preset = activePreset
        const kind = preset.kind
        const form = formFor(kind)
        const config = configByKind.get(kind)
        const isOidc = preset.protocol === 'OIDC'
        const busy = busyKind === kind
        // Google authentifie tout compte Google, un Entra `common` tout compte
        // Microsoft : sans liste de domaines, activer la carte ouvrirait
        // l'organisation (#127). L'API refuse l'activation, la carte le dit
        // avant d'y arriver.
        const domainsRequired = (preset.sharedIssuer || config?.requiresAllowedDomains)
          && form.allowedDomains.trim() === ''

        return (
          <Card key={kind} {...tourAnchor('sso.provider')}>
            <CardHeader className="flex-row items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                {preset.defaultLabel}
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {preset.protocol}
                </span>
              </CardTitle>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={e => set(kind)('enabled', e.target.checked)}
                />
                {t('sso.field.enabled')}
              </label>
            </CardHeader>

            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${kind}-label`}>{t('sso.field.label')}</Label>
                <Input
                  id={`${kind}-label`}
                  value={form.label}
                  onChange={e => set(kind)('label', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('sso.label.help')}</p>
              </div>

              {isOidc
                ? (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${kind}-issuer`}>{t('sso.field.issuer')}</Label>
                        <Input
                          id={`${kind}-issuer`}
                          value={form.issuer}
                          placeholder={preset.issuerHint ?? preset.defaultIssuer ?? ''}
                          onChange={e => set(kind)('issuer', e.target.value)}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor={`${kind}-clientId`}>{t('sso.field.clientId')}</Label>
                          <Input
                            id={`${kind}-clientId`}
                            value={form.clientId}
                            onChange={e => set(kind)('clientId', e.target.value)}
                          />
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor={`${kind}-clientSecret`}>{t('sso.field.clientSecret')}</Label>
                          <Input
                            id={`${kind}-clientSecret`}
                            type="password"
                            autoComplete="new-password"
                            value={form.clientSecret}
                            placeholder={config?.hasClientSecret ? '••••••••' : ''}
                            onChange={e => set(kind)('clientSecret', e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            {config?.hasClientSecret
                              ? t('sso.clientSecret.stored')
                              : t('sso.clientSecret.help')}
                          </p>
                        </div>
                      </div>
                    </>
                  )
                : (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${kind}-entryPoint`}>{t('sso.field.entryPoint')}</Label>
                        <Input
                          id={`${kind}-entryPoint`}
                          value={form.entryPoint}
                          onChange={e => set(kind)('entryPoint', e.target.value)}
                        />
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${kind}-idpIssuer`}>{t('sso.field.idpIssuer')}</Label>
                        <Input
                          id={`${kind}-idpIssuer`}
                          value={form.idpIssuer}
                          onChange={e => set(kind)('idpIssuer', e.target.value)}
                        />
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${kind}-idpCert`}>{t('sso.field.idpCert')}</Label>
                        <textarea
                          id={`${kind}-idpCert`}
                          value={form.idpCert}
                          onChange={e => set(kind)('idpCert', e.target.value)}
                          rows={5}
                          className="rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs"
                        />
                      </div>
                    </>
                  )}

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${kind}-domains`}>{t('sso.field.allowedDomains')}</Label>
                <Input
                  id={`${kind}-domains`}
                  value={form.allowedDomains}
                  placeholder="exemple.fr, filiale.exemple.fr"
                  onChange={e => set(kind)('allowedDomains', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('sso.allowedDomains.help')}</p>
                {domainsRequired && (
                  <p className="flex items-start gap-1.5 text-xs font-medium text-warning-text">
                    <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                    {t('sso.allowedDomains.required')}
                  </p>
                )}
              </div>

              {config && (
                <div className="flex flex-col gap-1 rounded-md bg-muted/50 px-3 py-2 text-xs">
                  <span className="font-semibold">{t('sso.idpSetup.title')}</span>
                  <code className="break-all">{config.callbackUrl}</code>
                  {config.metadataUrl && <code className="break-all">{config.metadataUrl}</code>}
                </div>
              )}

              <div className="flex justify-end gap-2">
                {config && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setPendingDelete(kind)}
                  >
                    <Trash2 className="mr-2 size-4" />
                    {t('common.delete')}
                  </Button>
                )}
                <Button type="button" disabled={busy} onClick={() => handleSave(kind)} {...tourAnchor('sso.save')}>
                  {busy
                    ? <Loader className="mr-2 size-4 animate-spin" />
                    : <Check className="mr-2 size-4" />}
                  {t('common.save')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      })()}

      {pendingDelete && (
        <SsoProviderDeleteModal
          open
          kind={pendingDelete}
          label={configByKind.get(pendingDelete)?.label
            ?? presets.find(preset => preset.kind === pendingDelete)?.defaultLabel
            ?? pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onSuccess={() => handleDeleted(pendingDelete)}
        />
      )}
    </div>
  )
}

export default SsoSettings
